import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useAppStore } from '../../state/store';
import { useCommandManager } from '../../state/commandStore';
import { useHelpData, useHelpCategoryLabel } from './useHelpData';
import { parseShortcutChips } from './helpData';
import type {
    HelpCategory,
    HelpCommand,
    HelpDocument,
    HelpFeature,
    HelpKeyBinding,
    HelpMcpTool,
    HelpReleaseNote,
} from './helpData';
import {
    FUNDAMENTALS_CATEGORY_ID,
    KEY_BINDINGS_CATEGORY_ID,
    MCP_TOOLS_CATEGORY_ID,
    OVERVIEW_CATEGORY_ID,
    WHATS_NEW_CATEGORY_ID,
} from './helpData';
import './HelpCenter.css';
import { Hint } from '@suite/panels/primitives/Hint';

/** Rows rendered per command list before the "Show all" affordance kicks in. */
const COMMAND_VISIBLE_LIMIT = 150;

/** Literal-typed sidebar group → translation key map (TypeScript-checked). */
const SIDEBAR_GROUP_KEY: Record<'browse' | 'commands' | 'reference', 'help.sidebarGroupBrowse' | 'help.sidebarGroupCommands' | 'help.sidebarGroupReference'> = {
    browse: 'help.sidebarGroupBrowse',
    commands: 'help.sidebarGroupCommands',
    reference: 'help.sidebarGroupReference',
};

/** Literal-typed release-note kind → translation key map (TypeScript-checked). */
const RELEASE_KIND_KEY: Record<HelpReleaseNote['kind'], 'help.releaseAdded' | 'help.releaseChanged' | 'help.releaseRemoved'> = {
    added: 'help.releaseAdded',
    changed: 'help.releaseChanged',
    removed: 'help.releaseRemoved',
};

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');
const SEARCH_CHORD_LABEL = IS_MAC ? '⌘K' : 'Ctrl K';

// ---------------------------------------------------------------------------
// Host component: global open state + the ⌘K / Ctrl+K entry chord
// ---------------------------------------------------------------------------

export function HelpCenterModal() {
    const { t } = useTranslation();
    const open = useAppStore((state) => state.helpCenterOpen);
    const setHelpCenterOpen = useAppStore((state) => state.setHelpCenterOpen);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
                if (useAppStore.getState().helpCenterOpen) return;
                event.preventDefault();
                setHelpCenterOpen(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [setHelpCenterOpen]);

    if (!open) return null;
    return <HelpCenterDialog onClose={() => setHelpCenterOpen(false)} />;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

function HelpCenterDialog({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const { doc, query, setQuery, categoryId, setCategoryId, results } = useHelpData();
    const categoryLabel = useHelpCategoryLabel();
    const searchInputRef = useRef<HTMLInputElement | null>(null); const previousFocusRef = useRef<HTMLElement | null>(null);
    const [showAllCommands, setShowAllCommands] = useState(false);

    // Focus the search field on open and hand focus back on close (HIG).
    useEffect(() => {
        previousFocusRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        searchInputRef.current?.focus();
        return () => {
            previousFocusRef.current?.focus();
        };
    }, []);

    // Window-level Esc fallback for when focus sits outside the search input.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    // While open, ⌘K / Ctrl+K refocuses the search field instead of reopening.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                searchInputRef.current?.focus();
                searchInputRef.current?.select();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    // Collapse the "show all" expansion whenever the result set changes.
    useEffect(() => {
        setShowAllCommands(false);
    }, [query, categoryId]);

    /**
     * Deep-linking "Go" action: close the panel first, then run the command
     * through the live registry (falling back to the command line dispatch
     * chain for canonical aliases), and flash the target element if the doc
     * declares one.
     */
    const runCommand = useCallback(
        (command: HelpCommand) => {
            onClose();
            window.setTimeout(() => {
                const manager = useCommandManager.getState();
                if (manager.commands[command.id]) {
                    manager.executeCommand(command.id);
                } else {
                    const alias = command.aliases?.[0];
                    if (alias) {
                        window.dispatchEvent(new CustomEvent('yap:run-command', { detail: { input: alias } }));
                    }
                }
                if (command.targetElementId) {
                    focusHelpTarget(command.targetElementId);
                }
            }, 60);
        },
        [onClose],
    );

    const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Escape') return;
        // Search field first: clear the term, then close on a second Esc.
        event.stopPropagation();
        if (query) {
            setQuery('');
        } else {
            onClose();
        }
    };

    const handleBackdropMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) onClose();
    };

    const isOverview = categoryId === OVERVIEW_CATEGORY_ID && query.trim() === '';
    const activeCategory = doc.categories.find((category) => category.id === categoryId);
    const visibleCommands = showAllCommands
        ? results.commands
        : results.commands.slice(0, COMMAND_VISIBLE_LIMIT);
    const hasResults =
        results.commands.length > 0 ||
        results.features.length > 0 ||
        results.keyBindings.length > 0 ||
        results.mcpTools.length > 0 ||
        results.releaseNotes.length >0; return (<div className="hc-backdrop" onMouseDown={handleBackdropMouseDown}>
            <section className="hc-panel" role="dialog" aria-modal="true" aria-label={t('help.helpCenter')}>
                <header className="hc-header">
                    <div className="hc-header-title">
                        <span className="hc-header-glyph" aria-hidden="true">?</span>
                        <h2 className="hc-header-heading">{t('help.helpCenter')}</h2>
                        {doc.meta.version && <span className="hc-version-pill">v{doc.meta.version}</span>}
                    </div>
                    <button type="button" className="hc-icon-button" onClick={onClose} aria-label={t('help.closeHelpCenter')}>
                        <CloseIcon />
                    </button>
                </header>

                <div className="hc-body">
                    <nav className="hc-sidebar" aria-label={t('help.helpCategories')}>
                        <div className="hc-search">
                            <span className="hc-search-icon" aria-hidden="true">
                                <SearchIcon />
                            </span>
                            <input
                                ref={searchInputRef}
                                className="hc-search-input"
                                type="text"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder={t('help.searchCommandsShortcuts')}
                                aria-label={t('help.searchTheHelpCenter')}
                                spellCheck={false}
                            />
                            {query ? (
                                <button
                                    type="button"
                                    className="hc-search-clear"
                                    onClick={() => setQuery('')}
                                    aria-label={t('help.clearSearch')}
                                >
                                    <CloseIcon />
                                </button>
                            ) : (
                                <kbd className="hc-kbd hc-kbd--hint">{SEARCH_CHORD_LABEL}</kbd>
                            )}
                        </div>

                        <div className="hc-sidebar-scroll">
                            {(['browse', 'commands', 'reference'] as const).map((group) => {
                                const categories = doc.categories.filter((category) =>category.group === group); if (categories.length === 0) return null; return (<div key={group} className="hc-sidebar-group">
                                        <p className="hc-sidebar-group-label">
                                            {t(SIDEBAR_GROUP_KEY[group])}
                                        </p>
                                        <ul className="hc-sidebar-list">
                                            {categories.map((category) => (
                                                <li key={category.id}>
                                                    <SidebarItem
                                                        category={category}
                                                        active={category.id === categoryId}
                                                        onSelect={setCategoryId}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </nav>

                    <main className="hc-main">
                        {isOverview ? (
                            <OverviewContent doc={doc} onOpenCategory={setCategoryId} />
                        ) : (
                            <>
                                <div className="hc-results-header">
                                    <h3 className="hc-results-title">
                                        {query.trim()
                                            ? t('help.resultsFor', { query: query.trim() })
                                            : activeCategory
                                                ? categoryLabel(activeCategory)
                                                : t('help.helpTitle')}
                                    </h3>
                                    {query.trim() && (
                                        <span className="hc-results-count">
                                            {results.totalMatches}{' '}
                                            {results.totalMatches === 1 ? t('help.match') : t('help.matches')}
                                        </span>
                                    )}
                                </div>

                                {!hasResults && <EmptyState query={query} />}

                                {results.features.length > 0 && (
                                    <ResultsSection label={t('help.features')} count={results.features.length}>
                                        {results.features.map((feature) => (
                                            <FeatureCard key={feature.id} feature={feature} />
                                        ))}
                                    </ResultsSection>
                                )}

                                {results.commands.length > 0 && (
                                    <ResultsSection label={t('help.commands')} count={results.commands.length}>
                                        {visibleCommands.map((command) => (
                                            <CommandRow key={`${command.source}-${command.id}`} command={command} onRun={runCommand} />
                                        ))}
                                        {results.commands.length > visibleCommands.length && (
                                            <button
                                                type="button"
                                                className="hc-show-more"
                                                onClick={() => setShowAllCommands(true)}
                                            >
                                                {t('help.showAllCommands', { count: results.commands.length })}
                                            </button>
                                        )}
                                    </ResultsSection>
                                )}

                                {results.keyBindings.length > 0 && (
                                    <ResultsSection label={t('help.keysMouse')} count={results.keyBindings.length}>
                                        {results.keyBindings.map((binding, index) => (
                                            <KeyBindingRow key={`${binding.key}-${index}`} binding={binding} />
                                        ))}
                                    </ResultsSection>
                                )}

                                {results.mcpTools.length > 0 && (
                                    <ResultsSection label={t('help.mCPTools')} count={results.mcpTools.length}>
                                        {results.mcpTools.map((tool) => (
                                            <McpToolRow key={tool.name} tool={tool} />
                                        ))}
                                    </ResultsSection>
                                )}

                                {results.releaseNotes.length > 0 && (
                                    <ResultsSection label={t('help.changes')} count={results.releaseNotes.length}>
                                        {results.releaseNotes.map((note, index) => (
                                            <ReleaseNoteRow key={`${note.kind}-${note.title}-${index}`} note={note} />
                                        ))}
                                    </ResultsSection>
                                )}
                            </>
                        )}
                    </main>
                </div>

                <footer className="hc-footer">
                    <span className="hc-footer-meta">
                        {t('help.footerStats', { commands: doc.commands.length, tools: doc.mcpTools.length })}
                        {doc.meta.generatedAt ? t('help.footerSnapshot', { date: doc.meta.generatedAt }) : ''}
                    </span>
                    <span className="hc-footer-hints">
                        <span className="hc-footer-hint">
                            <kbd className="hc-kbd hc-kbd--hint">{SEARCH_CHORD_LABEL}</kbd>{t('help.search')}</span>
                        <span className="hc-footer-hint">
                            <kbd className="hc-kbd hc-kbd--hint">{t('help.esc')}</kbd>{t('help.close')}</span>
                    </span>
                </footer>
            </section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

function SidebarItem({
    category,
    active,
    onSelect,
}: {
    category: HelpCategory;
    active: boolean;
    onSelect: (id: string) => void;
}) {
    const categoryLabel = useHelpCategoryLabel();
    return (
        <button
            type="button"
            className={`hc-sidebar-item${active ? ' hc-sidebar-item--active' : ''}`}
            onClick={() => onSelect(category.id)}
            aria-current={active ? 'true' : undefined}
        >
            <span className="hc-sidebar-item-label">{categoryLabel(category)}</span>
            {category.commandCount > 0 && <span className="hc-sidebar-count">{category.commandCount}</span>}
        </button>
    );
}

function ShortcutChips({ shortcut, title }: { shortcut: string | null | undefined; title?: string }) {
    const { t } = useTranslation();
    const chips = parseShortcutChips(shortcut);
    if (chips.length === 0) return null;
    return (
        <Hint label={title}>
        <span className="hc-kbd-group" aria-label={t('help.shortcutAria', { shortcut: shortcut ?? '' })}>
            {chips.map((chip, index) => (
                <span key={`${chip}-${index}`} className="hc-kbd-group-item">
                    {index > 0 && (
                        <span className="hc-kbd-plus" aria-hidden="true">
                            +
                        </span>
                    )}
                    <kbd className="hc-kbd">{chip}</kbd>
                </span>
            ))}
        </span>
        </Hint>
    );
}

function CommandRow({ command, onRun }: { command: HelpCommand; onRun: (command: HelpCommand) => void }) {
    const { t } = useTranslation();
    const modified = command.shortcutStatus === 'MODIFIED' || command.shortcutStatus === 'CUSTOM_ASSIGNED';
    return (
        <article className="hc-row">
            <div className="hc-row-main">
                <div className="hc-row-title">
                    <h4 className="hc-row-name">{command.name}</h4>
                    {modified && (
                        <span
                            className={`hc-pill hc-pill--${command.shortcutStatus === 'MODIFIED' ? 'modified' : 'custom'}`}
                        >
                            {command.shortcutStatus === 'MODIFIED'
                                ? t('help.shortcutModified')
                                : t('help.shortcutCustom')}
                        </span>
                    )}
                    {command.mutatesData && <span className="hc-pill hc-pill--neutral">{t('help.editsModel')}</span>}
                </div>
                <p className="hc-row-description">{command.description}</p>
                <p className="hc-row-path">
                    <code>{command.id}</code>
                    {command.uiPath ? ` · ${command.uiPath}` : ''}
                </p>
            </div>
            <div className="hc-row-actions">
                {command.activeShortcut && (
                    <ShortcutChips
                        shortcut={command.activeShortcut}
                        title={
                            command.shortcutStatus === 'MODIFIED' && command.defaultShortcut
                                ? t('help.factoryDefault', { shortcut: command.defaultShortcut })
                                : undefined
                        }
                    />
                )}
                <Hint label={t('help.runCommandTitle', { id: command.id })}>
                <button
                    type="button"
                    className="hc-go-button"
                    onClick={() => onRun(command)}
                    aria-label={t('help.runCommandAria', { name: command.name })}
                >{t('help.go')}<ArrowRightIcon />
                </button>
                </Hint>
            </div>
        </article>
    );
}

function FeatureCard({ feature }: { feature: HelpFeature }) {
    const { t } = useTranslation();
    const meta: Array<{ label: string; text: string }> = [
        { label: t('help.metaTrigger'), text: feature.trigger ?? '' },
        { label: t('help.metaSideEffects'), text: feature.sideEffects ?? '' },
        { label: t('help.metaParameters'), text: feature.parameters ?? '' },
        { label: t('help.metaEdgeCases'), text: feature.edgeCases ?? '' },
        ...feature.extras,
    ].filter((item) =>item.text); return (<article className="hc-feature-card">
            <header className="hc-feature-header">
                <span className="hc-feature-id">{feature.id}</span>
                <h4 className="hc-feature-title">{feature.title}</h4>
            </header>
            <p className="hc-feature-summary">{feature.summary}</p>
            {meta.length > 0 && (
                <dl className="hc-feature-meta">
                    {meta.map((item) => (
                        <div key={item.label} className="hc-feature-meta-row">
                            <dt>{item.label}</dt>
                            <dd>{item.text}</dd>
                        </div>
                    ))}
                </dl>
            )}
        </article>
    );
}

function KeyBindingRow({ binding }: { binding: HelpKeyBinding }) {
    const alternatives = binding.key.split('/').map((part) =>part.trim()).filter(Boolean); return (<article className="hc-row">
            <div className="hc-row-main">
                <div className="hc-kbd-group">
                    {alternatives.map((alternative, index) => (
                        <span key={`${alternative}-${index}`} className="hc-kbd-group-item">
                            {index > 0 && <span className="hc-kbd-plus">/</span>}
                            <kbd className="hc-kbd">{alternative}</kbd>
                        </span>
                    ))}
                </div>
                {binding.context && <p className="hc-row-context">{binding.context}</p>}
                <p className="hc-row-description">{binding.behavior}</p>
            </div>
        </article>
    );
}

function McpToolRow({ tool }: { tool: HelpMcpTool }) {
    const { t } = useTranslation();
    return (
        <article className="hc-row">
            <div className="hc-row-main">
                <div className="hc-row-title">
                    <h4 className="hc-row-name hc-row-name--mono">{tool.name}</h4>
                    <span className={`hc-pill hc-pill--${tool.mutatesData ? 'modified' : 'info'}`}>
                        {tool.mutatesData ? t('help.mutatesData') : t('help.readOnly')}
                    </span>
                </div>
                <p className="hc-row-description">{tool.description}</p>
            </div>
        </article>
    );
}

function ReleaseNoteRow({ note }: { note: HelpReleaseNote }) {
    const { t } = useTranslation();
    const pillClass =
        note.kind === 'added' ? 'hc-pill--new' : note.kind === 'changed' ? 'hc-pill--info' : 'hc-pill--removed';
    const pillLabel = t(RELEASE_KIND_KEY[note.kind]);
    return (
        <article className="hc-row">
            <div className="hc-row-main">
                <div className="hc-row-title">
                    <span className={`hc-pill ${pillClass}`}>{pillLabel}</span>
                    <h4 className="hc-row-name">{note.title}</h4>
                </div>
                {note.text && <p className="hc-row-description">{note.text}</p>}
            </div>
        </article>
    );
}

function OverviewContent({
    doc,
    onOpenCategory,
}: {
    doc: HelpDocument;
    onOpenCategory: (id: string) => void;
}) {
    const { t } = useTranslation();
    const categoryLabel = useHelpCategoryLabel();
    const browseCategories = doc.categories.filter(
        (category) =>category.group === 'commands' || category.id === FUNDAMENTALS_CATEGORY_ID || category.id === KEY_BINDINGS_CATEGORY_ID || category.id === MCP_TOOLS_CATEGORY_ID, ); return (<div className="hc-overview">
            <section className="hc-stats">
                <div className="hc-stat">
                    <span className="hc-stat-value">{doc.commands.length}</span>
                    <span className="hc-stat-label">{t('help.commands')}</span>
                </div>
                <div className="hc-stat">
                    <span className="hc-stat-value">{doc.mcpTools.length}</span>
                    <span className="hc-stat-label">{t('help.mCPAITools')}</span>
                </div>
                <div className="hc-stat">
                    <span className="hc-stat-value">{doc.meta.workbenches.length || 4}</span>
                    <span className="hc-stat-label">{t('help.workbenches')}</span>
                </div>
                <div className="hc-stat">
                    <span className="hc-stat-value">{doc.features.length}</span>
                    <span className="hc-stat-label">{t('help.featureGuides')}</span>
                </div>
            </section>

            <h3 className="hc-section-title">{t('help.browseTheDocumentation')}</h3>
            <div className="hc-cat-grid">
                {browseCategories.map((category) => (
                    <button
                        key={category.id}
                        type="button"
                        className="hc-cat-card"
                        onClick={() => onOpenCategory(category.id)}
                    >
                        <span className="hc-cat-card-label">{categoryLabel(category)}</span>
                        {category.commandCount > 0 && (
                            <span className="hc-cat-card-count">{category.commandCount}</span>
                        )}
                    </button>
                ))}
            </div>

            {doc.releaseNotes.length > 0 && (
                <>
                    <div className="hc-section-heading">
                        <h3 className="hc-section-title">{t('help.whatSNew')}</h3>
                        <button type="button" className="hc-link-button" onClick={() => onOpenCategory(WHATS_NEW_CATEGORY_ID)}>{t('help.seeAll')}</button>
                    </div>
                    <ResultsSection count={Math.min(6, doc.releaseNotes.length)} label="">
                        {doc.releaseNotes.slice(0, 6).map((note, index) => (
                            <ReleaseNoteRow key={`${note.kind}-${note.title}-${index}`} note={note} />
                        ))}
                    </ResultsSection>
                </>
            )}
        </div>
    );
}

function ResultsSection({
    label,
    count,
    children,
}: {
    label: string;
    count: number;
    children: ReactNode;
}) {
    return (
        <section className="hc-results-section">
            {label && (
                <h4 className="hc-section-title">
                    {label}
                    {count > 0 && <span className="hc-section-count">{count}</span>}
                </h4>
            )}
            <div className="hc-results-list">{children}</div>
        </section>
    );
}

function EmptyState({ query }: { query: string }) {
    const { t } = useTranslation();
    return (
        <div className="hc-empty">
            <span className="hc-empty-icon" aria-hidden="true">
                <SearchIcon />
            </span>
            <p className="hc-empty-title">{t('help.noResults')}</p>
            <p className="hc-empty-hint">
                {query.trim()
                    ? t('help.noMatchesFor', { query: query.trim() })
                    : t('help.categoryEmpty')}
            </p>
        </div>
    );
}

/** Scroll to and briefly highlight the element targeted by a help entry. */
function focusHelpTarget(elementId: string): void {
    const target = document.getElementById(elementId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('help-center-target-flash');
    window.setTimeout(() => target.classList.remove('help-center-target-flash'), 1800);
}

// ---------------------------------------------------------------------------
// Icons (inline SVG, currentColor)
// ---------------------------------------------------------------------------

function SearchIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

function ArrowRightIcon() {
    return (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <path
                d="M2 5.5H9M9 5.5L5.75 2.25M9 5.5L5.75 8.75"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
